import React, { useContext } from 'react';

import { Auth0Provider } from '@auth0/auth0-react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { Link, useLocation } from 'react-router-dom';
import { formatDateDetail } from '../../lib/hooks/utils';
import { Button } from '../../ui/components/ui/button';
import TopBanner from '../../ui/components/ui/top-banner';
import Sidebar from '../navigation/sidebar';
import { Context, ContextProvider } from './context';
import ErrorPage from './error-page';
import IntroOverlay from './IntroOverlay';

const AuthProvider = ({ children }: any) => {
  const redirectUri = window.location.origin;

  if (!(process.env.REACT_APP_AUTH0_DOMAIN && process.env.REACT_APP_AUTH0_ID && process.env.REACT_APP_AUTH0_AUDIENCE && redirectUri)) {
    return null;
  }

  const handleRedirectCallback = () => {
    sessionStorage.setItem('intro_pending', '1');
  };

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_ID}
      onRedirectCallback={handleRedirectCallback}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: 'openid profile email offline_access',
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        ui_locales: 'de'
      }}>
      {children}
    </Auth0Provider>
  );
};

const RootWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AuthProvider>
      <ContextProvider>
        <IntroOverlay />
        <Wrapper>{children}</Wrapper>
      </ContextProvider>
    </AuthProvider>
  );
};

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  const { isMobile, isTablet, canceledDate } = useContext(Context);

  const excludeSidebar = [
    '/register/informations',
    '/register/users',
    '/register/video',
    '/self-checkout/step1',
    '/self-checkout/step2',
    '/self-checkout/step3',
    '/reset',
    '/verification/mail',
    '/verification/2fa',
    '/access-expired'
  ];

  const excludePadding = ['/self-checkout/step1', '/self-checkout/step2', '/self-checkout/step3', '/verification/mail', '/reset'];

  const location = useLocation();

  // The public offer checkout has the token in its path.
  const isOfferCheckout = location.pathname.startsWith('/checkout/');
  const margin = !isOfferCheckout && !excludeSidebar.includes(location.pathname);

  try {
    return (
      <div className={`flex gap-4 w-full h-full min-h-[100vh] ${isMobile || isTablet ? 'flex-col' : ''}`}>
        {margin && <Sidebar />}
        {isMobile || isTablet ? (
          <div
            className={`min-h-[100vh] w-full relative ${canceledDate ? 'pt-24' : ''} ${isOfferCheckout || excludePadding.includes(location.pathname) ? '' : 'p-4'} `}>
            {canceledDate && (
              <div className="absolute -top-4 left-0 right-0 h-fit z-50 flex justify-center text-white">
                <div className="w-4/5 bg-gradient-to-r from-[#CE0909] to-[#900808] rounded-b-xl text-center p-2">
                  <div className="flex justify-center items-center h-full flex-col relative gap-1.5">
                    <p className="self-center text-[0.95rem]">Die Design Flatrate wurde zum {formatDateDetail(canceledDate)} gekündigt.</p>
                    <Link to="mailto:buchhaltung@7i7.de" className="">
                      <Button className="bg-white/50 hover:bg-white/40 border-white rounded-full text-white py-0 px-4 h-6 text-[0.8rem] border">
                        Jetzt Flatrate wieder aktivieren
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
            <TopBanner className="mb-8" />
            {children}
          </div>
        ) : (
          <div
            className={
              (margin ? 'ml-28 p-14 flex flex-col flex-grow min-h-screen' : '') + `min-h-[100vh] ${margin ? 'lg:max-w-[calc(100%-7em)]' : 'w-screen'}`
            }>
            <TopBanner className="mb-8" />
            {children}
          </div>
        )}
        {canceledDate && !isMobile && !isTablet && (
          <div className="absolute top-0 left-0 right-0 h-10 z-50 flex justify-center text-white">
            <div className="w-4/5 bg-gradient-to-r from-[#CE0909] to-[#900808] rounded-b-xl text-center">
              <div className="flex justify-center items-center h-full relative">
                <p className="self-center text-[0.95rem]">Die Design Flatrate wurde zum {formatDateDetail(canceledDate)} gekündigt.</p>
                <Link to="mailto:buchhaltung@7i7.de" className="absolute right-2">
                  <Button className="bg-white/50 hover:bg-white/40 border-white rounded-full text-white py-0 px-4 text-[0.8rem] h-6 border">
                    Jetzt Flatrate wieder aktivieren
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  } catch (error) {
    return <ErrorPage />;
  }
};

export default RootWrapper;
